import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { 
  Users, Plus, Edit, Mail, Phone, Building, 
  UserCheck, UserX, Shield, Search, ChevronRight, Copy, Link as LinkIcon
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const ROLES = [
  { value: "sales_negotiator", label: "Sales Negotiator" },
  { value: "senior_sales_negotiator", label: "Senior Sales Negotiator" },
  { value: "sales_progressor", label: "Sales Progressor" },
  { value: "lettings_negotiator", label: "Lettings Negotiator" },
  { value: "senior_lettings_negotiator", label: "Senior Lettings Negotiator" },
  { value: "lettings_manager", label: "Lettings Manager" },
  { value: "accounts", label: "Accounts" },
  { value: "inventory_clerk", label: "Inventory Clerk" },
  { value: "admin", label: "Administrator" },
  { value: "director", label: "Director" },
];

const ROLE_COLORS = {
  sales_negotiator: "bg-blue-100 text-blue-700",
  senior_sales_negotiator: "bg-blue-200 text-blue-800",
  sales_progressor: "bg-indigo-100 text-indigo-700",
  lettings_negotiator: "bg-green-100 text-green-700",
  senior_lettings_negotiator: "bg-green-200 text-green-800",
  lettings_manager: "bg-emerald-100 text-emerald-700",
  accounts: "bg-purple-100 text-purple-700",
  inventory_clerk: "bg-orange-100 text-orange-700",
  admin: "bg-slate-100 text-slate-700",
  director: "bg-yellow-100 text-yellow-700",
};

export default function StaffManagement() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };
  
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [saving, setSaving] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "sales_negotiator",
    phone: "",
    branch: "Head Office",
    commission_rate: 0
  });
  
  // Shown after creating a new staff member so admins can copy/share the
  // login details manually while outbound email is disabled.
  const [credentialResult, setCredentialResult] = useState(null);
  
  // Direct login URL for the current environment (preview vs prod auto-detected)
  const portalLoginUrl = typeof window !== 'undefined' ? `${window.location.origin}/login` : '/login';
  
  const generatePassword = () => {
    const pwd = `Goodacres${Math.random().toString(36).slice(2, 8)}!`;
    setFormData(prev => ({ ...prev, password: pwd }));
    toast.success('Suggested a temporary password');
  };
  
  useEffect(() => {
    fetchStaff();
  }, [includeInactive]);
  
  const fetchStaff = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/staff/list?include_inactive=${includeInactive}`,
        { headers }
      );
      if (res.ok) {
        const data = await res.json();
        setStaff(data.staff || []);
      }
    } catch (error) {
      console.error("Error fetching staff:", error);
      toast.error("Failed to load staff");
    } finally {
      setLoading(false);
    }
  };
  
  const handleOpenModal = (staffMember = null) => {
    if (staffMember) {
      setEditingStaff(staffMember);
      setFormData({
        email: staffMember.email || "",
        password: "", // Don't show existing password
        name: staffMember.name || "",
        role: staffMember.role || "sales_negotiator",
        phone: staffMember.phone || "",
        branch: staffMember.branch || "Head Office",
        commission_rate: staffMember.commission_rate ?? 0
      });
    } else {
      setEditingStaff(null);
      setFormData({
        email: "",
        password: "",
        name: "",
        role: "sales_negotiator",
        phone: "",
        branch: "Head Office",
        commission_rate: 0
      });
    }
    setShowModal(true);
  };
  
  const handleSave = async () => {
    if (!formData.name || !formData.email) {
      toast.error("Name and email are required");
      return;
    }
    
    if (!editingStaff && !formData.password) {
      toast.error("Password is required for new staff");
      return;
    }
    
    setSaving(true);
    try {
      if (editingStaff) {
        // Update existing staff
        const res = await fetch(`${API}/staff/${editingStaff.staff_id}`, {
          method: "PUT",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({
            name: formData.name,
            phone: formData.phone,
            branch: formData.branch,
            role: formData.role,
            commission_rate: Number(formData.commission_rate) || 0
          })
        });
        
        if (res.ok) {
          toast.success("Staff member updated");
          fetchStaff();
          setShowModal(false);
        } else {
          const error = await res.json();
          toast.error(error.detail || "Failed to update staff");
        }
      } else {
        // Create new staff
        const res = await fetch(`${API}/staff/create`, {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify(formData)
        });
        
        if (res.ok) {
          const created = await res.json();
          toast.success("Staff member created");
          fetchStaff();
          setShowModal(false);
          setCredentialResult({
            name: formData.name,
            email: formData.email,
            password: formData.password,
            role: getRoleLabel(formData.role),
            portal_url: portalLoginUrl,
            staff_id: created?.staff_id,
          });
        } else {
          const error = await res.json();
          toast.error(error.detail || "Failed to create staff");
        }
      }
    } catch (error) {
      toast.error("Error saving staff member");
    } finally {
      setSaving(false);
    }
  };
  
  const handleToggleActive = async (staffMember) => {
    try {
      const res = await fetch(`${API}/staff/${staffMember.staff_id}`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ is_active: !staffMember.is_active })
      });
      
      if (res.ok) {
        toast.success(`Staff member ${staffMember.is_active ? "deactivated" : "activated"}`);
        fetchStaff();
      }
    } catch (error) {
      toast.error("Failed to update staff status");
    }
  };
  
  const filteredStaff = staff.filter(s => 
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.role?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const getRoleLabel = (role) => {
    return ROLES.find(r => r.value === role)?.label || role;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="staff-management">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Staff Management</h1>
            <p className="text-slate-500">Manage team members and their roles</p>
          </div>
          <Button onClick={() => handleOpenModal()}>
            <Plus className="h-4 w-4 mr-2" />
            Add Staff Member
          </Button>
        </div>
        
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by name, email, or role..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={includeInactive}
                  onCheckedChange={setIncludeInactive}
                />
                <Label className="text-sm">Show inactive</Label>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Staff List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Team Members ({filteredStaff.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : filteredStaff.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No staff members found
              </div>
            ) : (
              <div className="divide-y">
                {filteredStaff.map((member) => (
                  <div 
                    key={member.staff_id}
                    className={`py-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 px-2 -mx-2 rounded-lg transition-colors ${!member.is_active ? "opacity-50" : ""}`}
                    onClick={() => navigate(`/staff/${member.user_id || member.staff_id}/profile`)}
                    data-testid={`staff-${member.staff_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`h-12 w-12 rounded-full flex items-center justify-center ${ROLE_COLORS[member.role] || "bg-slate-100"}`}>
                        <span className="text-lg font-semibold">
                          {member.name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{member.name}</span>
                          {!member.is_active && (
                            <Badge variant="outline" className="text-red-600">Inactive</Badge>
                          )}
                          {member.commission_eligible && (
                            <Badge variant="outline" className="text-green-600">Commission</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {member.email}
                          </span>
                          {member.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {member.phone}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Building className="h-3 w-3" />
                            {member.branch}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {Number(member.commission_rate || 0) > 0 && (
                        <Badge
                          className="bg-emerald-100 text-emerald-700"
                          title="Commission rate (% of fees generated)"
                          data-testid={`commission-rate-${member.staff_id}`}
                        >
                          {Number(member.commission_rate).toFixed(member.commission_rate % 1 ? 1 : 0)}%
                        </Badge>
                      )}
                      <Badge className={ROLE_COLORS[member.role] || "bg-slate-100"}>
                        {getRoleLabel(member.role)}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleOpenModal(member); }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleToggleActive(member); }}
                        className={member.is_active ? "text-red-600" : "text-green-600"}
                      >
                        {member.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                      </Button>
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingStaff ? "Edit Staff Member" : "Add Staff Member"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Smith"
              />
            </div>
            
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@agency.com"
                disabled={!!editingStaff}
              />
            </div>
            
            {!editingStaff && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Password *</Label>
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="text-xs text-blue-600 hover:underline"
                    data-testid="generate-password-btn"
                  >
                    Generate strong password
                  </button>
                </div>
                <Input
                  type="text"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Set a temporary password"
                  className="font-mono"
                />
                <p className="text-xs text-slate-500 mt-1">
                  You'll see the login details after creating so you can share them with the new team member.
                </p>
              </div>
            )}
            
            <div>
              <Label>Role</Label>
              <Select 
                value={formData.role} 
                onValueChange={(v) => setFormData({ ...formData, role: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="07700 123456"
              />
            </div>
            
            <div>
              <Label>Branch</Label>
              <Input
                value={formData.branch}
                onChange={(e) => setFormData({ ...formData, branch: e.target.value })}
                placeholder="Head Office"
              />
            </div>
            
            <div>
              <Label htmlFor="commission_rate">Commission Rate (% of fees generated)</Label>
              <div className="relative">
                <Input
                  id="commission_rate"
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={formData.commission_rate}
                  onChange={(e) => setFormData({ ...formData, commission_rate: e.target.value })}
                  placeholder="0"
                  className="pr-8"
                  data-testid="commission-rate-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Applied to fees this staff member generates each month. Set to 0 for non-commission roles.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : editingStaff ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staff credentials result - shown after create so admin can copy & share */}
      <Dialog open={!!credentialResult} onOpenChange={(open) => !open && setCredentialResult(null)}>
        <DialogContent className="max-w-md" data-testid="staff-credential-modal">
          {credentialResult && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-emerald-600" />
                  Staff Account Created
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <p className="text-sm text-slate-600">
                  Share the login details below with <strong>{credentialResult.name}</strong>. Outbound email is currently disabled, so the credentials are shown here for you to copy.
                </p>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Role</p>
                  <p className="text-sm font-medium">{credentialResult.role}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Email</p>
                  <div className="flex items-center justify-between gap-2">
                    <code className="text-sm font-medium text-slate-900 break-all">{credentialResult.email}</code>
                    <Button size="sm" variant="ghost" className="shrink-0"
                      onClick={() => { navigator.clipboard?.writeText(credentialResult.email); toast.success('Email copied'); }}>
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-blue-700 mb-2">Portal Login Link</p>
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 rounded-md bg-white p-1.5 border border-blue-200">
                      <QRCodeSVG value={credentialResult.portal_url} size={84} level="M" includeMargin={false} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <a href={credentialResult.portal_url} target="_blank" rel="noopener noreferrer"
                          className="text-xs font-medium text-blue-700 hover:text-blue-900 underline break-all flex-1">
                          {credentialResult.portal_url}
                        </a>
                        <Button size="sm" variant="ghost" className="shrink-0 -mt-1"
                          onClick={() => { navigator.clipboard?.writeText(credentialResult.portal_url); toast.success('Link copied'); }}>
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <p className="text-[11px] text-blue-700/80 mt-2">Scan the QR with a phone camera to open the login page instantly.</p>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-amber-700 mb-1">Temporary Password</p>
                  <div className="flex items-center justify-between gap-2">
                    <code className="text-sm font-bold text-slate-900 break-all">{credentialResult.password}</code>
                    <Button size="sm" variant="ghost" className="shrink-0"
                      onClick={() => { navigator.clipboard?.writeText(credentialResult.password); toast.success('Password copied'); }}>
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <p className="text-xs text-amber-700 mt-2">
                    Please ask them to change this password on first login.
                  </p>
                </div>
                <Button variant="outline" className="w-full"
                  onClick={() => {
                    const msg = [
                      `Hi ${credentialResult.name.split(' ')[0]},`,
                      '',
                      `Welcome to the Goodacres portal — you've been set up as ${credentialResult.role}.`,
                      '',
                      `Login: ${credentialResult.portal_url}`,
                      `Email: ${credentialResult.email}`,
                      `Temporary password: ${credentialResult.password}`,
                      '',
                      'Please change your password on first login.',
                      '',
                      'Goodacres Team'
                    ].join('\n');
                    navigator.clipboard?.writeText(msg);
                    toast.success('Welcome message copied');
                  }}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy welcome message
                </Button>
              </div>
              <DialogFooter>
                <Button onClick={() => setCredentialResult(null)}>Done</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
